/**
 * dom.js — Akili Brain: DOM Injection & Element Cache
 * Creates the full FAB + drawer HTML structure. CSS is NOT injected here;
 * it is loaded via /public/css/ai/assistant.css through the master CSS files.
 *
 * ── Self-Evolve additions ────────────────────────────────────────────────────
 *   • WORKFLOWS_ICON, PRIVACY_ICON SVG constants
 *   • dom.workflowsBtn, dom.privacyBtn element getters
 *   • Workflows button injected into drawer header (before back-btn)
 *   • Privacy button injected above the disclaimer text
 *   • Self-Evolve component styles live in assistant-self-evolve.css
 *     and are imported by assistant.css — no runtime injection needed
 *
 * FIX (missing dom getters):
 *   Added seven getters that were referenced throughout the codebase but absent
 *   from the dom object, causing silent failures:
 *     dom.workflowsPanel  → #ai-workflows
 *     dom.personalFactsPanel → #ai-personalfacts
 *     dom.wfList          → #ai-wf-list
 *     dom.wfNewBtn        → #ai-wf-new-btn
 *     dom.wfForm          → #ai-wf-form
 *     dom.pfList          → #ai-pf-list   (renderPersonalFactsList bailed here)
 *     dom.toolStatus      → #ai-tool-status
 *
 * FIX (missing panel HTML):
 *   Three panel elements were never injected into #ai-drawer:
 *     #ai-tool-status     — tool-call status bar
 *     #ai-workflows       — scheduled workflows panel + creation form
 *     #ai-personalfacts   — memory / personal-facts panel
 *   These are inserted immediately after #ai-insights and before #ai-doc-chip.
 *
 * FIX (missing panel CSS):
 *   .ai-workflows and .ai-personalfacts display rules live in assistant-self-evolve.css
 *   so the panels are hidden by default and revealed via the .open class.
 */

import { state } from './state';
import { injectNewsElements } from './dom-news';

// ── SVG assets ────────────────────────────────────────────────────────────────
const FAB_FACE_SVG = (
  '<svg class="ai-fab-face" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" overflow="visible">' +
    '<defs>' +
      '<radialGradient id="fab-inner-fill" cx="50%" cy="42%" r="58%">' +
        '<stop offset="0%"   stop-color="#001E33" stop-opacity="1"/>' +
        '<stop offset="65%"  stop-color="#010C1A" stop-opacity="1"/>' +
        '<stop offset="100%" stop-color="#000408" stop-opacity="1"/>' +
      '</radialGradient>' +
      '<filter id="fab-glow-sm" x="-120%" y="-120%" width="340%" height="340%">' +
        '<feGaussianBlur stdDeviation="1" result="b"/>' +
        '<feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>' +
      '</filter>' +
      '<filter id="fab-glow-md" x="-150%" y="-150%" width="400%" height="400%">' +
        '<feGaussianBlur stdDeviation="2" result="b"/>' +
        '<feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>' +
      '</filter>' +
    '</defs>' +
    '<!-- Outer hex ring with gold vertex markers (slow clockwise spin) -->' +
    '<g class="fab-outer-ring">' +
      '<polygon points="20,4 33.9,12 33.9,28 20,36 6.1,28 6.1,12" ' +
        'stroke="#00E5FF" stroke-width="0.55" fill="none" opacity="0.42" stroke-dasharray="3.5 5.5"/>' +
      '<circle cx="20"   cy="4"  r="1.1" fill="#C9A96E" opacity="0.85" filter="url(#fab-glow-sm)"/>' +
      '<circle cx="33.9" cy="12" r="1"   fill="#C9A96E" opacity="0.6"  filter="url(#fab-glow-sm)"/>' +
      '<circle cx="33.9" cy="28" r="1"   fill="#C9A96E" opacity="0.6"  filter="url(#fab-glow-sm)"/>' +
      '<circle cx="20"   cy="36" r="1.1" fill="#C9A96E" opacity="0.85" filter="url(#fab-glow-sm)"/>' +
      '<circle cx="6.1"  cy="28" r="1"   fill="#C9A96E" opacity="0.6"  filter="url(#fab-glow-sm)"/>' +
      '<circle cx="6.1"  cy="12" r="1"   fill="#C9A96E" opacity="0.6"  filter="url(#fab-glow-sm)"/>' +
    '</g>' +
    '<!-- Middle hex ring (counter-spin, gold) -->' +
    '<g class="fab-mid-ring">' +
      '<polygon points="32,20 26,30.4 14,30.4 8,20 14,9.6 26,9.6" ' +
        'stroke="#C9A96E" stroke-width="0.5" fill="none" opacity="0.28" stroke-dasharray="2 7"/>' +
    '</g>' +
    '<!-- Inner solid hex — the core stage -->' +
    '<polygon class="fab-inner-hex" ' +
      'points="20,12.5 26.5,16.25 26.5,23.75 20,27.5 13.5,23.75 13.5,16.25" ' +
      'fill="url(#fab-inner-fill)" stroke="rgba(0,229,255,.6)" stroke-width="0.75"/>' +
    '<!-- The Akili "A" sigil -->' +
    '<g class="fab-sigil" stroke-linecap="round" stroke-linejoin="round">' +
      '<line x1="15.6" y1="25.5" x2="20"   y2="14.2" stroke="#00E5FF" stroke-width="1.15" opacity="0.95"/>' +
      '<line x1="20"   y1="14.2" x2="24.4" y2="25.5" stroke="#00E5FF" stroke-width="1.15" opacity="0.95"/>' +
      '<line x1="17.1" y1="21.9" x2="22.9" y2="21.9" stroke="#C9A96E" stroke-width="0.95" opacity="0.92"/>' +
    '</g>' +
    '<!-- Apex glow -->' +
    '<circle class="fab-apex" cx="20" cy="14.2" r="1.5" fill="#00E5FF" opacity="0.88" filter="url(#fab-glow-md)"/>' +
    '<circle cx="20" cy="14.2" r="0.72" fill="#ffffff" opacity="1"/>' +
  '</svg>'
);

const HDR_FACE_SVG = (
  '<svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" style="width:32px;height:32px">' +
    '<polygon points="16,3.2 27.1,9.6 27.1,22.4 16,28.8 4.9,22.4 4.9,9.6" ' +
      'stroke="#00E5FF" stroke-width="0.55" fill="none" opacity="0.5" stroke-dasharray="2.5 4"/>' +
    '<polygon points="25.6,16 20.8,24.3 11.2,24.3 6.4,16 11.2,7.7 20.8,7.7" ' +
      'stroke="#C9A96E" stroke-width="0.5" fill="none" opacity="0.32" stroke-dasharray="1.5 5.5"/>' +
    '<polygon points="16,10 21.2,13 21.2,19 16,22 10.8,19 10.8,13" ' +
      'fill="rgba(0,14,26,.92)" stroke="rgba(0,229,255,.58)" stroke-width="0.65"/>' +
    '<g stroke-linecap="round">' +
      '<line x1="12.5" y1="20.3" x2="16"   y2="11.3" stroke="#00E5FF" stroke-width="0.95" opacity="0.95"/>' +
      '<line x1="16"   y1="11.3" x2="19.5" y2="20.3" stroke="#00E5FF" stroke-width="0.95" opacity="0.95"/>' +
      '<line x1="13.7" y1="17.4" x2="18.3" y2="17.4" stroke="#C9A96E" stroke-width="0.8"  opacity="0.9"/>' +
    '</g>' +
    '<circle cx="16" cy="11.3" r="1.1" fill="#00E5FF" opacity="0.9"/>' +
  '</svg>'
);

const SEND_ICON       = '<svg viewBox="0 0 24 24"><path d="M2 21l21-9L2 3v7l15 2-15 2z"/></svg>';
const CLOSE_ICON      = '<svg viewBox="0 0 24 24"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>';
const CTX_ICON        = '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/></svg>';
const HIST_ICON       = '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M13 3a9 9 0 0 0-9 9H1l3.89 3.89.07.14L9 12H6c0-3.87 3.13-7 7-7s7 3.13 7 7-3.13 7-7 7c-1.93 0-3.68-.79-4.94-2.06l-1.42 1.42A8.954 8.954 0 0 0 13 21a9 9 0 0 0 0-18zm-1 5v5l4.28 2.54.72-1.21-3.5-2.08V8H12z"/></svg>';
const BACK_ICON       = '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/></svg>';
const TRASH_ICON      = '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/></svg>';
const PLUS_ICON       = '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/></svg>';
const INSIGHTS_ICON   = '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/></svg>';
const PAPERCLIP_ICON  = '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M13.5 7.5l-6.5 6.5a3.5 3.5 0 01-4.95-4.95l6.5-6.5a2 2 0 012.83 2.83L5.4 11.8a.5.5 0 01-.71-.71l5.2-5.2"/></svg>';
const MICROPHONE_ICON = '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect x="5" y="1" width="6" height="9" rx="3"/><path d="M2 8a6 6 0 0012 0"/><line x1="8" y1="14" x2="8" y2="16"/><line x1="5" y1="16" x2="11" y2="16"/></svg>';

// ── Self-Evolve SVG icons ─────────────────────────────────────────────────────
const WORKFLOWS_ICON = (
  '<svg viewBox="0 0 24 24" fill="currentColor">' +
    '<path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5' +
    'c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z"/>' +
  '</svg>'
);

const PRIVACY_ICON = (
  '<svg viewBox="0 0 24 24" fill="currentColor">' +
    '<path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z' +
    'm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"/>' +
  '</svg>'
);

// ── Lazy DOM element cache ────────────────────────────────────────────────────
const _cache = {};
function el(id) {
  if (!_cache[id]) _cache[id] = document.getElementById(id);
  return _cache[id];
}

export const dom = {
  get fab()           { return el('ai-fab'); },
  get drawer()        { return el('ai-drawer'); },
  get backdrop()      { return el('ai-backdrop'); },
  get msgs()          { return el('ai-msgs'); },
  get msgsContainer() { return el('ai-msgs-container'); },
  get input()         { return el('ai-input'); },
  get sendBtn()       { return el('ai-send'); },
  get closeBtn()      { return el('ai-close-btn'); },
  get histBtn()       { return el('ai-hist-btn'); },
  get insightsBtn()   { return el('ai-insights-btn'); },
  get backBtn()       { return el('ai-back-btn'); },
  get ctxBar()        { return el('ai-ctx-bar'); },
  get ctxText()       { return el('ai-ctx-text'); },
  get suggestions()   { return el('ai-suggestions'); },
  get history()       { return el('ai-history'); },
  get insights()      { return el('ai-insights'); },
  get convList()      { return el('ai-conv-list'); },
  get insList()       { return el('ai-ins-list'); },
  get insBadge()      { return el('ai-ins-badge'); },
  get fabBadge()      { return el('ai-fab-badge'); },
  get docChip()       { return el('ai-doc-chip'); },
  get docChipName()   { return el('ai-doc-chip-name'); },
  get docChipRm()     { return el('ai-doc-chip-rm'); },
  get attachBtn()     { return el('ai-attach-btn'); },
  get micBtn()        { return el('ai-mic-btn'); },
  get docFile()       { return el('ai-doc-file'); },
  get inputRow()      { return el('ai-input-row'); },
  get modeBar()       { return el('ai-mode-bar'); },
  get modeChatBtn()   { return el('ai-mode-chat'); },
  get modeDraftBtn()  { return el('ai-mode-draft'); },
  get modePrepBtn()   { return el('ai-mode-prep'); },
  get draftType()     { return el('ai-draft-type'); },
  get prepType()      { return el('ai-prep-type'); },
  get avatar3d()      { return el('ai-avatar-3d'); },
  get newConvBtn()    { return el('ai-new-conv-btn'); },
  get markAllRead()   { return el('ai-mark-all-read-btn'); },
  get proactivePanel(){ return el('ai-proactive-panel'); },
  get psCards()       { return el('ps-cards'); },
  get psPill()        { return el('ps-pill'); },
  get psCollapse()    { return el('ps-collapse'); },
  get fabRmNew()      { return el('fab-rm-new'); },
  get fabRmHist()     { return el('fab-rm-hist'); },
  get fabRmVoice()    { return el('fab-rm-voice'); },

  // ── Self-Evolve ─────────────────────────────────────────────────────────────
  /** Workflows button in drawer header (hidden for client role). */
  get workflowsBtn()      { return el('ai-workflows-btn'); },
  /** Privacy / personal-facts button above the disclaimer. */
  get privacyBtn()        { return el('ai-privacy-btn'); },
  /** Signal Feed (news) button in drawer header. */
  get newsBtn()           { return el('ai-news-btn'); },

  // ── FIX: previously missing getters — caused silent failures throughout ──────
  /** Workflows inline panel (#ai-workflows). Used by all view-switchers. */
  get workflowsPanel()    { return el('ai-workflows'); },
  /** Personal-facts / memory panel (#ai-personalfacts). Used by all view-switchers. */
  get personalFactsPanel(){ return el('ai-personalfacts'); },
  /** Workflows list container (#ai-wf-list). Used by renderWorkflowsList(). */
  get wfList()            { return el('ai-wf-list'); },
  /** Workflows "New" button (#ai-wf-new-btn). Used by wireWorkflowForm(). */
  get wfNewBtn()          { return el('ai-wf-new-btn'); },
  /** Workflow creation form (#ai-wf-form). Used by wireWorkflowForm() and event handlers. */
  get wfForm()            { return el('ai-wf-form'); },
  /**
   * Personal-facts list container (#ai-pf-list).
   * renderPersonalFactsList() and loadPersonalFacts() both guard on this —
   * when it was undefined, both functions returned immediately without
   * making any API call or rendering any content.
   */
  get pfList()            { return el('ai-pf-list'); },
  /** Tool-call status bar (#ai-tool-status). Used by setToolCallStatus(). */
  get toolStatus()        { return el('ai-tool-status'); },

  // ── Personalization Roadmap ─────────────────────────────────────────────────
  /** Thread recall banner injected above the mode bar. */
  get threadRecallBanner() { return el('ai-thread-recall'); },
  /** Mini widget floating near the FAB (glanceable state). */
  get miniWidget()         { return el('ai-mini-widget'); },
  /** Persona card container inside the privacy panel. */
  get personaSelector()    { return el('ai-persona-selector'); },
  /** Notification settings section inside the privacy panel. */
  get notifSettings()      { return el('ai-notif-settings'); },

  /** Invalidate cached reference (call after dynamic DOM changes) */
  invalidate(id) { delete _cache[id]; },

  /** Kili Sentient: Legal Risk Radar fingerprint panel. */
  get riskFingerprint() { return el('kili-risk-fingerprint'); },
  /** Kili Sentient: Morning brief card container. */
  get morningBrief()    { return el('kili-morning-brief'); },

  /** Exported SVG constants for use by other modules */
  TRASH_ICON,
};

// ── Self-Evolve injected CSS ──────────────────────────────────────────────────

// ── DOM injection ─────────────────────────────────────────────────────────────
export function injectDOM() {
  if (document.getElementById('ai-fab')) return;

  const frag = document.createDocumentFragment();

  // FAB
  const fab = document.createElement('button');
  fab.id = 'ai-fab';
  fab.setAttribute('aria-label', 'Open Kili AI Assistant');
  fab.setAttribute('aria-haspopup', 'dialog');
  fab.setAttribute('aria-expanded', 'false');
  // NOTE: radial menu is intentionally NOT inside fab.innerHTML.
  // overflow:hidden on the FAB clips any child content that extends beyond the
  // 68 px circle. The menu is created as a sibling element instead, anchored
  // via position:fixed in CSS so it floats above the FAB without clipping.
  fab.innerHTML = [
    FAB_FACE_SVG,
    '<span class="ai-fab-badge" id="ai-fab-badge" aria-hidden="true"></span>',
  ].join('');

  // Radial menu — sibling of #ai-fab, anchored via fixed positioning in CSS
  const radialMenu = document.createElement('div');
  radialMenu.className = 'fab-radial-menu';
  radialMenu.setAttribute('role', 'menu');
  radialMenu.innerHTML = [
    '<button class="fab-radial-item" id="fab-rm-new"   title="New Chat" type="button">&#10010;</button>',
    '<button class="fab-radial-item" id="fab-rm-hist"  title="History"  type="button">&#9202;</button>',
    '<button class="fab-radial-item" id="fab-rm-voice" title="Voice"    type="button">&#127908;</button>',
  ].join('');

  // Backdrop
  const backdrop = document.createElement('div');
  backdrop.id = 'ai-backdrop';
  backdrop.setAttribute('aria-hidden', 'true');

  // Drawer
  const drawer = document.createElement('div');
  drawer.id = 'ai-drawer';
  drawer.setAttribute('role', 'dialog');
  drawer.setAttribute('aria-label', 'Kili AI Assistant');
  drawer.setAttribute('aria-modal', 'true');
  drawer.innerHTML = [
    '<div class="drawer-edge-glow" aria-hidden="true"></div>',
    // Header
    '<div class="ai-hdr">',
      '<div class="ai-avatar-3d" id="ai-avatar-3d">' + HDR_FACE_SVG + '</div>',
      '<div class="ai-hdr-info">',
        '<div class="ai-hdr-name">Kili</div>',
        '<div class="ai-hdr-sub">',
          '<span class="ai-online-dot" aria-hidden="true"></span>',
          'AI-powered legal assistant',
        '</div>',
      '</div>',
      '<div class="ai-hdr-actions">',
        '<button class="ai-hdr-btn" id="ai-hist-btn" title="Conversation History" aria-label="Conversation History">' + HIST_ICON + '</button>',
        '<span id="ai-ins-badge-wrap" class="ai-hdr-btn" style="padding:0;cursor:default;">',
          '<button class="ai-hdr-btn" id="ai-insights-btn" title="Insights" aria-label="Insights" style="padding:5px;">' + INSIGHTS_ICON + '</button>',
          '<span id="ai-ins-badge" aria-label="Unread insights"></span>',
        '</span>',
        // ── Self-Evolve: Workflows button (hidden for clients in main.js init) ──
        '<button class="ai-hdr-btn" id="ai-workflows-btn" title="Scheduled Workflows" aria-label="Scheduled Workflows">' + WORKFLOWS_ICON + '</button>',
        '<button class="ai-hdr-btn" id="ai-back-btn" style="display:none;" title="Back to Chat" aria-label="Back to Chat">' + BACK_ICON + '</button>',
        '<button class="ai-close-btn" id="ai-close-btn" aria-label="Close AI Assistant">' + CLOSE_ICON + '</button>',
      '</div>',
    '</div>',
    // Context bar
    '<div class="ai-ctx-bar" id="ai-ctx-bar" aria-live="polite">',
      CTX_ICON + '<span id="ai-ctx-text">Platform Assistant</span>',
    '</div>',
    // Agent 1 — Proactive Suggestion Panel
    '<div class="ps-pill" id="ps-pill" role="button" tabindex="0" aria-label="Show Kili suggestions">',
      '<span class="ps-pill-dot"></span>',
      '<span class="ps-pill-text">💡 Kili has suggestions</span>',
      '<span class="ps-pill-expand">▸</span>',
    '</div>',
    '<div id="ai-proactive-panel" aria-live="polite">',
      '<div class="ps-header">',
        '<span class="ps-label">💡 Kili suggests</span>',
        '<button class="ps-collapse" id="ps-collapse" aria-label="Collapse suggestions" title="Collapse">▾</button>',
      '</div>',
      '<div id="ps-cards"></div>',
    '</div>',
    // Chat messages — tool-status bar is first child so it appears above messages
    '<div class="ai-msgs-container" id="ai-msgs-container">',
      // Tool-call status bar — shown while an AI tool call is in progress.
      // Placed INSIDE the msgs container so it renders in the message flow, not
      // outside the scroll area. dom.toolStatus getter continues to work by ID.
      '<div class="ai-tool-status-bar" id="ai-tool-status" style="display:none" aria-live="polite">',
        '<span class="ai-tool-status-dot"></span>',
        '<span id="ai-tool-status-text"></span>',
      '</div>',
      '<div class="ai-msgs" id="ai-msgs" role="log" aria-live="polite" aria-label="Chat messages"></div>',
      '<div class="ai-suggestions" id="ai-suggestions"></div>',
    '</div>',
    // History panel
    '<div class="ai-history" id="ai-history">',
      '<div class="ai-history-hdr">',
        '<span class="ai-history-title">Recent Conversations</span>',
        '<button class="ai-new-conv-btn" id="ai-new-conv-btn" aria-label="New Chat" title="New Chat">' + PLUS_ICON + '</button>',
      '</div>',
      '<div class="ai-conv-list" id="ai-conv-list">',
        '<div class="ai-conv-loading">Loading conversations&hellip;</div>',
      '</div>',
    '</div>',
    // Insights panel
    '<div class="ai-insights" id="ai-insights">',
      '<div class="ai-insights-hdr">',
        '<span class="ai-insights-title">Insights</span>',
        '<button class="ai-mark-read-btn" id="ai-mark-all-read-btn">Mark all read</button>',
      '</div>',
      '<div class="ai-ins-list" id="ai-ins-list">',
        '<div class="ai-ins-loading">Loading insights&hellip;</div>',
      '</div>',
    '</div>',

    // ── FIX: Three panels that were previously missing from the DOM ───────────
    // Without these elements, dom.toolStatus / dom.wfList / dom.pfList all
    // returned undefined, causing setToolCallStatus(), renderWorkflowsList(),
    // and renderPersonalFactsList() to bail out silently on startup.

    // NOTE: #ai-tool-status was moved inside #ai-msgs-container (see above).
    // It is no longer declared here to avoid a duplicate ID in the DOM.

    // Workflows panel
    '<div class="ai-workflows" id="ai-workflows">',
      '<div class="ai-panel-hdr">',
        '<span class="ai-panel-title">Scheduled Workflows</span>',
        '<button class="ai-wf-new-btn" id="ai-wf-new-btn" type="button"',
          ' aria-label="New workflow" title="Create new workflow">',
          '+ New',
        '</button>',
      '</div>',
      '<div id="ai-wf-list" class="ai-wf-list">',
        '<div class="ai-panel-loading">Loading&hellip;</div>',
      '</div>',
      // Workflow creation form (hidden by default; toggled by wireWorkflowForm())
      '<div class="ai-wf-form" id="ai-wf-form" style="display:none">',
        '<div class="ai-wf-form-inner">',
          '<div class="ai-form-group">',
            '<label class="ai-form-label" for="ai-wf-name">Workflow name</label>',
            '<input class="ai-form-input" id="ai-wf-name" type="text"',
              ' placeholder="e.g. Monday case-law digest" autocomplete="off"/>',
          '</div>',
          '<div class="ai-form-group">',
            '<label class="ai-form-label" for="ai-wf-type">Type</label>',
            '<select class="ai-form-select" id="ai-wf-type">',
              '<option value="case_law_alert">⚖️ Case Law Alert</option>',
              '<option value="deadline_summary">📅 Deadline Summary</option>',
              '<option value="custom_prompt">✏️ Custom Prompt</option>',
            '</select>',
          '</div>',
          '<div class="ai-form-group" id="ai-wf-prompt-row" style="display:none">',
            '<label class="ai-form-label" for="ai-wf-prompt">Custom prompt</label>',
            '<textarea class="ai-form-textarea" id="ai-wf-prompt"',
              ' placeholder="What should Kili do on this schedule?"></textarea>',
          '</div>',
          '<div class="ai-form-group">',
            '<label class="ai-form-label" for="ai-wf-cron">Schedule (cron)</label>',
            '<input class="ai-form-input" id="ai-wf-cron" type="text"',
              ' placeholder="0 9 * * 1" value="0 9 * * 1" autocomplete="off"/>',
            '<div class="ai-cron-presets">',
              '<button class="ai-cron-preset ai-wf-preset-btn" type="button" data-cron="0 9 * * 1">Mon 9 am</button>',
              '<button class="ai-cron-preset ai-wf-preset-btn" type="button" data-cron="0 9 * * *">Daily 9 am</button>',
              '<button class="ai-cron-preset ai-wf-preset-btn" type="button" data-cron="0 9 * * 5">Fri 9 am</button>',
              '<button class="ai-cron-preset ai-wf-preset-btn" type="button" data-cron="0 9 * * 1-5">Weekdays 9 am</button>',
            '</div>',
          '</div>',
          '<div class="ai-form-group">',
            '<label class="ai-form-label" for="ai-wf-matter">Matter ID (optional)</label>',
            '<input class="ai-form-input" id="ai-wf-matter" type="text"',
              ' placeholder="Leave blank for all matters" autocomplete="off"/>',
          '</div>',
          '<div class="ai-modal-footer" style="padding:10px 0 2px;">',
            '<button class="ai-btn ai-btn-ghost ai-btn-sm" type="button"',
              ' id="ai-wf-cancel-btn">Cancel</button>',
            '<button class="ai-btn ai-btn-primary ai-btn-sm" type="button"',
              ' id="ai-wf-save-btn">Create Workflow</button>',
          '</div>',
        '</div>',
      '</div>',
    '</div>',

    // Personal Facts / Memory panel — expanded with persona + notification sections
    // [PHASE 5] Persona selector + [PHASE 3] Notification settings sit at the top
    '<div class="ai-personalfacts" id="ai-personalfacts">',

      // Persona selector
      '<div class="ai-pf-section" id="ai-persona-selector">',
        '<div class="ai-pf-section-title">',
          '<svg viewBox="0 0 16 16" fill="currentColor" style="width:13px;height:13px">',
            '<path d="M8 8a3 3 0 100-6 3 3 0 000 6zm-5 6s-1 0-1-1 1-4 6-4 6 3 6 4-1 1-1 1H3z"/>',
          '</svg>',
          "Kili's Personality",
        '</div>',
        '<p class="ai-pf-section-desc">Choose how Kili communicates with you. Takes effect from the next message.</p>',
        '<div class="ai-persona-cards" role="radiogroup" aria-label="Choose Kili\'s personality">',
          // Strategic Advisor
          '<div class="ai-persona-card" data-persona="strategic_advisor" role="radio" aria-checked="false" tabindex="0" title="Formal, data-driven, structured">',
            '<div class="ai-persona-card-icon"><svg viewBox="0 0 20 20" fill="currentColor"><path d="M2 10a8 8 0 1116 0A8 8 0 012 10zm8-4a1 1 0 100 2 1 1 0 000-2zm-1 4a1 1 0 012 0v4a1 1 0 11-2 0v-4z"/></svg></div>',
            '<div class="ai-persona-card-body">',
              '<div class="ai-persona-card-name">Strategic Advisor</div>',
              '<div class="ai-persona-card-desc">Formal, data-driven, structured output. Best for complex analysis.</div>',
            '</div>',
            '<div class="ai-persona-card-check" aria-hidden="true"><svg viewBox="0 0 16 16" fill="currentColor"><path d="M13.78 4.22a.75.75 0 010 1.06l-7.25 7.25a.75.75 0 01-1.06 0L2.22 9.28a.75.75 0 011.06-1.06L6 10.94l6.72-6.72a.75.75 0 011.06 0z"/></svg></div>',
          '</div>',
          // Efficiency Expert (default active)
          '<div class="ai-persona-card active" data-persona="efficiency_expert" role="radio" aria-checked="true" tabindex="0" title="Concise, bullet-point oriented, action-first">',
            '<div class="ai-persona-card-icon"><svg viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clip-rule="evenodd"/></svg></div>',
            '<div class="ai-persona-card-body">',
              '<div class="ai-persona-card-name">Efficiency Expert</div>',
              '<div class="ai-persona-card-desc">Concise, bullet-point oriented. Action items first, no fluff.</div>',
            '</div>',
            '<div class="ai-persona-card-check" aria-hidden="true"><svg viewBox="0 0 16 16" fill="currentColor"><path d="M13.78 4.22a.75.75 0 010 1.06l-7.25 7.25a.75.75 0 01-1.06 0L2.22 9.28a.75.75 0 011.06-1.06L6 10.94l6.72-6.72a.75.75 0 011.06 0z"/></svg></div>',
          '</div>',
          // Empathetic Partner
          '<div class="ai-persona-card" data-persona="empathetic_partner" role="radio" aria-checked="false" tabindex="0" title="Warm, supportive, acknowledges the human element first">',
            '<div class="ai-persona-card-icon"><svg viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clip-rule="evenodd"/></svg></div>',
            '<div class="ai-persona-card-body">',
              '<div class="ai-persona-card-name">Empathetic Partner</div>',
              '<div class="ai-persona-card-desc">Warm and supportive. Acknowledges the human element before solving.</div>',
            '</div>',
            '<div class="ai-persona-card-check" aria-hidden="true"><svg viewBox="0 0 16 16" fill="currentColor"><path d="M13.78 4.22a.75.75 0 010 1.06l-7.25 7.25a.75.75 0 01-1.06 0L2.22 9.28a.75.75 0 011.06-1.06L6 10.94l6.72-6.72a.75.75 0 011.06 0z"/></svg></div>',
          '</div>',
        '</div>',
      '</div>',

      // Notification settings
      '<div class="ai-pf-section" id="ai-notif-settings">',
        '<div class="ai-pf-section-title">',
          '<svg viewBox="0 0 16 16" fill="currentColor" style="width:13px;height:13px">',
            '<path d="M8 16a2 2 0 001.985-1.75c.016-.164-.133-.25-.235-.25h-3.5c-.102 0-.25.086-.235.25A2 2 0 008 16zM1.794 7.084C2.55 5.31 4.108 3.978 6 3.52V3a2 2 0 114 0v.52c1.892.457 3.45 1.789 4.206 3.564.06.144.03.312-.094.417-.57.483-1.612 1.5-2.112 2.999H3.998C3.5 9.003 2.458 7.985 1.888 7.501a.375.375 0 01-.094-.417z"/>',
          '</svg>',
          'Notifications',
        '</div>',
        '<div class="ai-notif-row">',
          '<label class="ai-notif-label" for="ai-notif-briefing-time">Morning briefing time</label>',
          '<select class="ai-form-input ai-notif-select" id="ai-notif-briefing-time" aria-label="Daily briefing time">',
            '<option value="07:00">7:00 AM</option>',
            '<option value="07:30">7:30 AM</option>',
            '<option value="08:00" selected>8:00 AM</option>',
            '<option value="08:30">8:30 AM</option>',
            '<option value="09:00">9:00 AM</option>',
            '<option value="09:30">9:30 AM</option>',
            '<option value="10:00">10:00 AM</option>',
            '<option value="10:30">10:30 AM</option>',
          '</select>',
        '</div>',
        '<div class="ai-notif-row ai-notif-toggle-row">',
          '<label class="ai-notif-label" for="ai-notif-deadline-reminders">Deadline reminders</label>',
          '<label class="ai-toggle" aria-label="Toggle deadline reminders">',
            '<input type="checkbox" id="ai-notif-deadline-reminders" checked aria-label="Deadline reminders">',
            '<span class="ai-toggle-slider"></span>',
          '</label>',
        '</div>',
        '<div class="ai-notif-row ai-notif-toggle-row">',
          '<label class="ai-notif-label" for="ai-notif-weekly-digest">Weekly digest</label>',
          '<label class="ai-toggle" aria-label="Toggle weekly digest">',
            '<input type="checkbox" id="ai-notif-weekly-digest" aria-label="Weekly digest">',
            '<span class="ai-toggle-slider"></span>',
          '</label>',
        '</div>',
      '</div>',

      // Memory / personal facts list (unchanged)
      '<div id="ai-pf-list" class="ai-pf-list"></div>',

    '</div>',

    // ── End of FIX ────────────────────────────────────────────────────────────

    // Document chip
    '<div class="ai-doc-chip" id="ai-doc-chip">',
      '<svg viewBox="0 0 16 16" fill="currentColor" style="width:11px;height:11px;flex-shrink:0">',
        '<path d="M9 1H3a1 1 0 00-1 1v12a1 1 0 001 1h10a1 1 0 001-1V6L9 1z"/>',
        '<path d="M9 1v5h5" fill="none" stroke="currentColor" stroke-width="1.2"/>',
      '</svg>',
      '<span class="ai-doc-chip-name" id="ai-doc-chip-name">document.pdf</span>',
      '<button class="ai-doc-chip-rm" id="ai-doc-chip-rm" aria-label="Remove document" title="Remove">&#x2715;</button>',
    '</div>',
    // Mode toggle + draft/prep options
    '<div class="ai-mode-bar" id="ai-mode-bar">',
      '<button class="ai-mode-btn active" id="ai-mode-chat" data-mode="chat">Chat</button>',
      '<button class="ai-mode-btn" id="ai-mode-draft" data-mode="draft">Draft</button>',
      '<button class="ai-mode-btn" id="ai-mode-prep" data-mode="prep">Prep</button>',
      '<select class="ai-draft-type" id="ai-draft-type" style="display:none">',
        '<option value="letter">Letter</option>',
        '<option value="memo">Memo</option>',
        '<option value="contract_clause">Contract Clause</option>',
        '<option value="case_summary">Case Summary</option>',
        '<option value="legal_opinion">Legal Opinion</option>',
      '</select>',
      '<select class="ai-prep-type" id="ai-prep-type" style="display:none">',
        '<option value="argument">Draft Argument</option>',
        '<option value="counter">Counter\u2011Arguments</option>',
        '<option value="statutory">Interpret Statute</option>',
        '<option value="compare">Compare Jurisdictions</option>',
      '</select>',
    '</div>',
    // Input row
    '<div class="ai-input-row" id="ai-input-row">',
      '<button id="ai-attach-btn" aria-label="Attach a document (Ctrl+Click for recent docs)" title="Attach document (Ctrl+Click for recent)">',
        PAPERCLIP_ICON,
      '</button>',
      '<button id="ai-mic-btn" aria-label="Voice input" title="Record voice message">',
        MICROPHONE_ICON,
      '</button>',
      '<input type="file" id="ai-doc-file" accept=".pdf,.docx,.txt,.md,.csv" style="display:none" aria-hidden="true">',
      '<textarea id="ai-input" placeholder="Ask me anything about the platform\u2026" rows="1" ',
        'aria-label="Message to AI assistant" autocomplete="off" spellcheck="true"></textarea>',
      '<button id="ai-send" aria-label="Send message" disabled>' + SEND_ICON + '</button>',
    '</div>',
    // ── Self-Evolve: Privacy button ──────────────────────────────────────────
    '<button class="ai-privacy-link" id="ai-privacy-btn" aria-label="Manage what Kili remembers about you">',
      PRIVACY_ICON,
      'What Kili remembers',
    '</button>',
    '<div class="ai-disclaimer">Kili can make mistakes. Verify critical legal information with your advocate.</div>',
  ].join('');

  frag.appendChild(fab);

  // [PHASE 4] Mini widget — sits just above the FAB, shows glanceable state
  const _mwEl = document.createElement('div');
  _mwEl.id        = 'ai-mini-widget';
  _mwEl.className = 'ai-mini-widget';
  _mwEl.setAttribute('aria-label', 'Kili AI quick glance');
  _mwEl.setAttribute('role', 'status');
  _mwEl.style.display = 'none';
  _mwEl.innerHTML = [
    '<div class="ai-mini-widget-inner">',
      '<span class="ai-mini-widget-unread"   id="ai-mw-unread"    aria-label="Unread messages"></span>',
      '<span class="ai-mini-widget-deadlines" id="ai-mw-deadlines" aria-label="Upcoming deadlines"></span>',
      '<span class="ai-mini-widget-action"   id="ai-mw-action"></span>',
    '</div>',
  ].join('');
  frag.appendChild(_mwEl);

  frag.appendChild(radialMenu);  // sibling of FAB; menu escapes overflow:hidden
  frag.appendChild(backdrop);
  frag.appendChild(drawer);
  document.body.appendChild(frag);

  // ── Awareness Pipeline: inject news panel + header button ─────────────────
  injectNewsElements();
}

// ── Edge glow helper ─────────────────────────────────────────────────────────
export function triggerEdgeGlow() {
  const drawer = dom.drawer;
  if (!drawer) return;
  drawer.classList.remove('new-message');
  void drawer.offsetWidth; // force reflow
  drawer.classList.add('new-message');
  setTimeout(() => drawer.classList.remove('new-message'), 3200);
}


// ── [PHASE 5] Persona avatar + colour adaptation ──────────────────────────────

const _PERSONA_COLORS = {
  strategic_advisor:  '#4F8EF7',
  efficiency_expert:  '#00E5FF',
  empathetic_partner: '#C9A96E',
};

/**
 * Update the assistant's visual accent when the persona changes.
 * Applies a CSS custom property on the drawer so all accent-dependent
 * child elements pick it up via var(--persona-accent).
 * @param {'strategic_advisor'|'efficiency_expert'|'empathetic_partner'} persona
 */
export function updateAvatarForPersona(persona) {
  const drawer = document.getElementById('ai-drawer');
  if (!drawer) return;
  const accent = _PERSONA_COLORS[persona];
  if (!accent) return;
  drawer.style.setProperty('--persona-accent', accent);
  drawer.dataset.persona = persona;
  const fab = document.getElementById('ai-fab');
  if (fab) {
    Object.keys(_PERSONA_COLORS).forEach(p => fab.classList.remove('persona-' + p));
    fab.classList.add('persona-' + persona);
  }
}

/**
 * [PHASE 4] HTML string for the mini widget element.
 * Also injected inline in injectDOM() above — exported for main.js fallback.
 */
export const MINI_WIDGET_HTML = [
  '<div id="ai-mini-widget" class="ai-mini-widget" aria-label="Kili AI quick glance" role="status" style="display:none">',
    '<div class="ai-mini-widget-inner">',
      '<span class="ai-mini-widget-unread"    id="ai-mw-unread"    aria-label="Unread messages"></span>',
      '<span class="ai-mini-widget-deadlines" id="ai-mw-deadlines" aria-label="Upcoming deadlines"></span>',
      '<span class="ai-mini-widget-action"    id="ai-mw-action"></span>',
    '</div>',
  '</div>',
].join('');
